import React, { useState } from "react";
import moment from "moment";
import TimeRound from "@rsuite/icons/TimeRound";
import { Message, Tooltip, Whisper } from "rsuite";
import TableWrapper from "../common/TableWrapper";
import { Link } from "react-router-dom";
import Button from "../common/Button";
import ServiceShareModal from "../modals/ServiceShareModal";
import ConfirmationModal from "../modals/ConfirmationModal";
import NetworkService from "../../NetworkService";
import { useGlobalContext } from "../../GlobalContext";
import { getRideOrderApiErrorMessage } from "../ride-order/rideOrderPayload";
import { CAR_TYPES, toasterFunction } from "../utils";
import styles from "./TableStyles.module.scss";
import { getRideLabel, isRideFinishedOrCanceled, isScheduledRide } from "./rideStatus";
import { markRideCanceledByUser } from "../hooks/useRideCancelNotifications";

function parsePromoCodesContact(promoCodes) {
  if (!promoCodes || typeof promoCodes !== "string") return null;

  try {
    const parsed = JSON.parse(promoCodes);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function RecentRides({ data, loading, onPaginationChange, pagination, hidePagination, onRidesChange }) {
  const { user: userState } = useGlobalContext();
  const [admin] = userState;
  const companyAdminId = admin?.user?.id;

  const [shareRideId, setShareRideId] = useState(null);
  const [cancelRideId, setCancelRideId] = useState(null);
  const [isCanceling, setIsCanceling] = useState(false);

  const activePage = data?.current_page;
  const totalElements = data?.total;
  const pageSize = pagination?.size || data?.per_page;

  const handleCancelConfirm = async () => {
    if (!cancelRideId || !companyAdminId) return;

    const rideId = cancelRideId;

    setIsCanceling(true);
    try {
      markRideCanceledByUser(rideId);
      await NetworkService.cancelCompanyRide(rideId, companyAdminId);
      setCancelRideId(null);
      toasterFunction(
        <Message closable showIcon type="success">
          Service #{rideId} successfully canceled.
        </Message>
      );
      onRidesChange?.();
    } catch (error) {
      toasterFunction(
        <Message closable showIcon type="error">
          {getRideOrderApiErrorMessage(error)}
        </Message>
      );
    } finally {
      setIsCanceling(false);
    }
  };

  const columns = [
    {
      header: "Service",
      render: (e) => (
        <div className={styles.serviceCell}>
          <div className={styles.serviceIdRow}>
            <b>{e.id}</b>
            {isScheduledRide(e) && (
              <Whisper
                placement="top"
                trigger={["hover", "click"]}
                speaker={<Tooltip>Scheduled Ride</Tooltip>}
              >
                <button
                  type="button"
                  className={styles.scheduledRideIconBtn}
                  aria-label="Scheduled Ride"
                >
                  <TimeRound />
                </button>
              </Whisper>
            )}
          </div>
          <div>{moment(e.created_at).format("DD/MM/YYYY HH:mm")}</div>
          {!!e.contact && <div className={styles.contact}>• For someone else</div>}
        </div>
      ),
      options: {
        width: 180,
      },
    },
    {
      header: "User",
      render: (e) => (
        <>
          <Link to={`/users/${e.user_id}`}>{e?.user?.firstName + " " + e?.user?.lastName}</Link>
        </>
      ),
      options: {
        width: 200,
      },
    },
    {
      header: "Driver",
      render: (e) => {
        if (!e?.driver) return null;

        const driverName = [e.driver.firstName, e.driver.lastName].filter(Boolean).join(" ");

        return (
          <div className={styles.driverCell}>
            {driverName && <div className={styles.driverName}>{driverName}</div>}
            {e.driver.phone && <div className={styles.driverPhone}>{e.driver.phone}</div>}
          </div>
        );
      },
      options: {
        width: 180,
      },
    },
    {
      header: "Status",
      render: (e) => getRideLabel(e),
      options: {
        width: 200,
      },
    },
    {
      header: "From",
      dataKey: "location",
      options: {
        width: 200,
      },
    },
    {
      header: "To",
      render: (e) => e.final_destination_address || e.destination,
      options: {
        width: 200,
      },
    },
    {
      header: "Class",
      render: (e) => CAR_TYPES.find((car) => car.value == e.car_type)?.label,
      options: {
        width: 120,
      },
    },
    {
      header: "Ordered for someone else",
      render: (e) => {
        const contact = parsePromoCodesContact(e.promo_codes);
        if (!contact) return null;

        const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ");
        if (!name && !contact.phone) return null;

        return (
          <div>
            {name}
            {name && contact.phone ? <br /> : null}
            {contact.phone}
          </div>
        );
      },
      options: {
        width: 250,
      },
    },
    {
      header: "Actions",
      render: (e) => {
        if (isRideFinishedOrCanceled(e)) return null;

        const isRowCanceling = isCanceling && cancelRideId === e.id;

        return (
          <>
            <Button
              variant="white"
              disabled={isCanceling}
              onClick={() => setCancelRideId(e.id)}
            >
              {isRowCanceling ? "Canceling..." : "Cancel"}
            </Button>
            <Button variant="white" disabled={isCanceling} onClick={() => setShareRideId(e.id)}>
              Share
            </Button>
          </>
        );
      },
      options: {
        width: 200,
        fixed: "right",
      },
    },
  ];

  return (
    <>
      <ConfirmationModal
        isOpen={cancelRideId != null}
        close={() => {
          if (!isCanceling) setCancelRideId(null);
        }}
        handleYes={handleCancelConfirm}
        header={`Cancel service #${cancelRideId}?`}
        subHeader="This will cancel the active service request. This action cannot be undone."
        yesLabel="Yes, cancel"
        noLabel="No"
        isSubmitting={isCanceling}
      />
      <ServiceShareModal
        isOpen={shareRideId != null}
        close={() => setShareRideId(null)}
        rideId={shareRideId}
      />
      <TableWrapper
        loading={loading}
        totalElements={totalElements}
        pageSize={pageSize}
        activePage={activePage}
        columns={columns}
        emptyMessage="No services yet"
        onChange={onPaginationChange}
        data={data?.data || []}
        hidePagination={hidePagination}
      />
    </>
  );
}

export default RecentRides;
